/**
 * An extended Promise that provides a cancellation flag. Note that this is not
 * a fully general cancelable promise, but requires an animating caller to
 * reject the promise on cancellation.
 */
export class RoulerPromise extends Promise<void> {
    canceled: boolean = false;
}

/**
 * This is an Error subclass that lets callers filter errors based on their
 * type.
 */
export class RoulerCanceled extends Error {
    constructor() {
        super('Rouler Canceled');
    }
}

/**
 * Executes a callback on every frame for a given number of milliseconds.
 * @param duration - how long to run the animation, measured in milliseconds.
 * @param fn - the callback to execute with parameter `t` representing the time
 * elapsed on the unit interval (0, 1], open on the zero side, meaning that `t`
 * skips 0.
 */
export const rouler = (duration: number, fn: (t: number) => void) => {
    const start = Date.now();

    let p: RoulerPromise, update: () => void;
    p = new RoulerPromise((resolve, reject) => {
        update = () => {
            if (p.canceled) {
                reject(new RoulerCanceled());
                return;
            }
            let delta = Date.now() - start;
            if (delta < duration) {
                window.requestAnimationFrame(update);
                fn(delta / duration);
            } else {
                fn(1);
                resolve();
            }
        };
    });
    update();
    return p;
};
